import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';

import { getAemContentPolarisTabCollectionQuery } from '../queries';
import { DtlTabVariables, DTLUxlResponse } from './types';

/**
 * Base/fallback CF variation always requested alongside the resolved region.
 * AEM returns the region-specific variation when present and falls back to this
 * `master` variation otherwise. Per the agreed GraphQL contract, the `region`
 * filter is always sent with both the resolved region (when available) and
 * `master`.
 */
const MASTER_VARIATION = 'master';

export function getUxlVariablesForDTL(tabTitles: string | string[], region?: string): DtlTabVariables {
  // Convert single tab to array for uniform handling
  const tabs = Array.isArray(tabTitles) ? tabTitles : [tabTitles];

  return {
    input: {
      queryName: 'tabcollectionlist',
      filters: [
        {
          name: 'tags',
          expressions: tabs.map(tab => ({
            value: tab.toLowerCase(),
            operator: 'EQUALS',
          })),
        },
        // Geo-targeting region. The UXL gateway's `AemContentByQueryNameInput`
        // schema only exposes `filters`, `locale`, `orderBy`, `queryName` — there
        // is NO top-level `region` field (passing one yields BAD_USER_INPUT), so
        // region travels as a dedicated filter. Per the agreed contract this
        // filter is ALWAYS present and carries the resolved region (when one was
        // resolved) plus the `master` fallback variation, both as EQUALS.
        {
          name: 'region',
          expressions: [
            ...(region
              ? [
                  {
                    value: region,
                    operator: 'EQUALS',
                  },
                ]
              : []),
            {
              value: MASTER_VARIATION,
              operator: 'EQUALS',
            },
          ],
        },
      ],
    },
  };
}

export function useGetUxlForDTL(): (tabTitles: string | string[], region?: string) => Promise<DTLUxlResponse | null> {
  const client = useApolloClient();
  const requestId = useClientEnvVarsStore(state => state.envVarsObject?.['REQUEST_ID']);

  return useCallback(
    async (tabTitles: string | string[], region?: string) => {
      try {
        const result = await client.query<DTLUxlResponse, DtlTabVariables>({
          query: getAemContentPolarisTabCollectionQuery,
          variables: getUxlVariablesForDTL(tabTitles, region),
          fetchPolicy: 'network-only',
          errorPolicy: 'all',
          context: {
            headers: {
              ...(requestId && { 'x-request-id': requestId }),
            },
          },
        });
        const { data, error } = result;
        if (error) {
          console.error('[useGetUxlForDTL] Error calling Apollo query for DTL operation:', error);
          return null;
        }
        return data ?? null;
      } catch (error) {
        console.error('[useGetUxlForDTL] Error calling Apollo query for DTL operation:', error);
        return null;
      }
    },
    [client]
  );
}
